import { getCasesQueryOptions } from "@/queries";
import type { CaseSearchParamSchema } from "@/schemas";
import type { Case } from "@/types";
import { useSuspenseInfiniteQuery } from "@tanstack/react-query";
import { useLocation, useSearch } from "@tanstack/react-router";
import { useVirtualizer } from "@tanstack/react-virtual";
import { useEffect, useMemo, useRef } from "react";
import { useDebounce } from "./useDebounce";

type VirtualItem =
	| {
			type: "header";
			label: string;
			id: string;
	  }
	| {
			type: "item";
			data: Case;
			id: string;
	  };

interface UseCasesProps {
	generateList?: (search: string, items: Case[]) => VirtualItem[];
}

function useCases(props: UseCasesProps = {}) {
	const { generateList } = props;
	const scrollRef = useRef<HTMLDivElement>(null);
	const status = useLocation({
		select: (s) => (s.pathname.endsWith("closed") ? "closed" : "active"),
	});
	const searchQ = useSearch({
		select: (state: CaseSearchParamSchema) => state.q ?? "",
	});
	const debouncedSearch = useDebounce(searchQ, 300);
	const {
		data: cases,
		isLoading,
		isFetching,
		isFetchingNextPage,
		hasNextPage,
		fetchNextPage,
	} = useSuspenseInfiniteQuery(
		getCasesQueryOptions({
			status,
		}),
	);
	const caseItems = useMemo(() => {
		if (!cases.length) return [];
		if (generateList) return generateList(debouncedSearch, cases);
		if (debouncedSearch) {
			return cases.filter(
				(caseItem) =>
					caseItem.title
						.toLowerCase()
						.includes(debouncedSearch.toLowerCase()) ||
					caseItem.summary
						?.toLowerCase()
						?.includes(debouncedSearch.toLowerCase()) ||
					caseItem.patient?.name
						?.toLowerCase()
						?.includes(debouncedSearch.toLowerCase()) ||
					caseItem.patient?.surname
						?.toLowerCase()
						?.includes(debouncedSearch.toLowerCase()),
			);
		}
		return cases;
	}, [cases, debouncedSearch, generateList]);
	const casesVirtualizer = useVirtualizer({
		count: caseItems.length,
		gap: 12,
		overscan: 5,
		paddingStart: 16,
		paddingEnd: 16,
		getScrollElement: () => scrollRef.current,
		estimateSize: () => 125,
		measureElement: (el) => el.getBoundingClientRect().height,
	});
	const casesVirtualizerItems = casesVirtualizer.getVirtualItems();
	const isEmpty = useMemo(
		() => !isLoading && !isFetching && !caseItems.length && !searchQ,
		[isLoading, isFetching, searchQ, caseItems],
	);
	const isEmptySearch = useMemo(
		() => !isLoading && !isFetching && !caseItems.length && !!searchQ,
		[isLoading, isFetching, searchQ, caseItems],
	);
	useEffect(() => {
		const [lastItem] = [...casesVirtualizerItems].reverse();

		if (!lastItem) return;

		if (
			lastItem.index >= cases.length - 1 &&
			hasNextPage &&
			!isFetchingNextPage
		) {
			fetchNextPage();
		}
	}, [
		cases.length,
		hasNextPage,
		fetchNextPage,
		isFetchingNextPage,
		casesVirtualizerItems,
	]);

	return {
		scrollRef,
		searchQ,
		caseItems,
		isLoading,
		isFetchingNextPage,
		isEmpty,
		isEmptySearch,
		casesVirtualizer,
		casesVirtualizerItems,
	};
}

export default useCases;
